import React, {useCallback, useEffect, useState} from 'react';
import Grid from "@mui/material/Grid";
import {Autocomplete, TextField} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import MenuItem from "@mui/material/MenuItem";
import dayjs from "dayjs";
import ENUMS from "../../General/Enums";
import {matchIsValidTel, MuiTelInput} from 'mui-tel-input'
import {formValidations} from "../CVTemplate";
import OrangeSwitch from "../../OrangeSwitch";
import Chip from "@mui/material/Chip";

const Gender = [
    { value: "", label: "Select gender" },
    { value: "Female", label: "Female" },
    { value: "Male", label: "Male" },
    { value: "Other", label: "Other" },
];

const Ethnicity = [
    { value: "", label: "Select ethnicity" },
    { value: "Asian", label: "Asian" },
    { value: "White", label: "White" },
    { value: "Coloured", label: "Coloured" },
    { value: "Black", label: "Black" },
    { value: "Hispanic", label: "Hispanic" },
];

export const style = {
    "& label.Mui-focused": { color: "#FF6600" },
    "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#FF6600" } },
};

const validateEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
const validateIdNumber = (idNumber) => /^[0-9]{13}$/.test(idNumber);
const validatePhoneNumber = (phoneNumber) => phoneNumber !== null && matchIsValidTel(phoneNumber);

export default function PersonalDetails({ personalDetails, updateCV, userHasClickedNext, readOnly }) {
    const [formData, setFormData] = useState(personalDetails);
    const [secondaryLanguage, setSecondaryLanguage] = useState(
        typeof personalDetails.secondaryLanguage === 'string' && personalDetails.secondaryLanguage !== ""
                ? personalDetails.secondaryLanguage.split(',').map(item => item.trim())
                : []
    );
    const [validEmail, setValidEmail] = useState(() => {
        return personalDetails.email ? validateEmail(personalDetails.email) : true;
    });
    const [validIdNumber, setValidIdNumber] = useState(() => {
        return personalDetails.idNumber ? validateIdNumber(personalDetails.idNumber) : true;
    });
    const [validPhone, setValidPhone] = useState(() => {
        return personalDetails.phoneNum ? validatePhoneNumber(personalDetails.phoneNum) : true;
    });

    const checkRequiredData = useCallback(() => {
        let isValid = validateEmail(formData.email)
            && validatePhoneNumber(formData.phoneNum)
            && validateIdNumber(formData.idNumber);
        const requiredFields = [
            'candidateSummary',
            'name', 'surname',
            'idNumber',
            'dob',
            'gender',
            'phoneNum',
            'email',
            'primaryLanguage',
            'address',
            'city',
            'province',
            'ethnicity'
        ];

        for (let field of requiredFields) {
            if (formData[field] == null || formData[field] === '') {
                isValid = false;
                break;
            }
        }
        if (formData.disabled && (!formData.disabilityDescription || formData.disabilityDescription === '')) {
            isValid = false;
        }

        formValidations[1].valid = isValid;
    }, [formData]);

    useEffect(() => {
        checkRequiredData();
    }, [checkRequiredData]);

    const handleChange = useCallback((e) => {
        const { name, value, checked, type } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData((prev) => ({
            ...prev,
            [name]: newValue,
        }));
        if (name === 'email') {
            setValidEmail(validateEmail(value));
        }

        if (name === 'idNumber') {
            setValidIdNumber(validateIdNumber(value));
        }
        updateCV('personalDetails', { ...formData, [name]: newValue });
    }, [formData, updateCV]);

    const handleSecondaryLanguageChange = async (value) => {
        const selectedLanguages = typeof value === 'string' ? value.split(',') : value;
        setSecondaryLanguage(selectedLanguages);

        setFormData((prev) => ({
            ...prev,
            secondaryLanguage: selectedLanguages.join(','),
        }));

        updateCV('personalDetails', {
            ...formData,
            secondaryLanguage: selectedLanguages.join(','),
        });
    }

    const handlePhoneNumberChange = useCallback((value) => {
        setValidPhone(matchIsValidTel(value));
        setFormData((prev) => ({
            ...prev,
            phoneNum: value,
        }));
        updateCV('personalDetails', { ...formData, phoneNum: value });
    }, [formData, updateCV]);

    const handleDateChange = useCallback((e) => {
        setFormData((prev) => ({
            ...prev,
            dob: e,
        }));
        updateCV('personalDetails', { ...formData, dob: e });
    }, [formData, updateCV]);

    return (
        <Grid container direction="row" spacing={2} padding={2} justifyContent="flex-start" alignContent="center">
            <Grid item xs={12}>
                <h3 data-testid="HeadingPersonalDetails">Personal Details</h3>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Candidate summary"
                    multiline
                    minRows={4}
                    required
                    error={userHasClickedNext && !formData.candidateSummary}
                    sx={style}
                    variant={readOnly ? "standard" : "outlined"}
                    style={{ width: "99%" }}
                    value={formData.candidateSummary ?? ""}
                    onChange={handleChange}
                    id="candidateSummary"
                    data-testid="candidateSummary"
                    name="candidateSummary"
                />
            </Grid>
            <Grid item xs={12}><h5>Full Name</h5></Grid>
            <Grid item xs={3}>
                <TextField
                    style={{ width: "99%" }}
                    sx={style}
                    label="First name"
                    required
                    error={userHasClickedNext && !formData.name}
                    variant={readOnly ? "standard" : "outlined"}
                    value={formData.name ?? ""}
                    data-testid="name"
                    onChange={handleChange}
                    name="name"
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    style={{ width: "99%" }}
                    sx={style}
                    required
                    error={userHasClickedNext && !formData.surname}
                    label="Last name"
                    variant={readOnly ? "standard" : "outlined"}
                    value={formData.surname ?? ""}
                    data-testid="surname"
                    onChange={handleChange}
                    name="surname"
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    style={{ width: "99%" }}
                    sx={style}
                    label="Name known by"
                    variant={readOnly ? "standard" : "outlined"}
                    value={formData.preferredName ?? ""}
                    data-testid="preferredName"
                    onChange={handleChange}
                    name="preferredName"
                />
            </Grid>
            <Grid item xs={12}><h5>Identity</h5></Grid>
            <Grid item xs={3}>
                <FormControlLabel
                    label="South African citizen?"
                    labelPlacement="end"
                    value={formData.rsaCitizen}
                    checked={formData.rsaCitizen}
                    onChange={handleChange}
                    name="rsaCitizen"
                    data-testid="rsaCitizen"
                    control={<OrangeSwitch />}
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    style={{ width: "99%" }}
                    sx={style}
                    required={formData.rsaCitizen}
                    error={userHasClickedNext && formData.rsaCitizen && !formData.idNumber}
                    label="ID Number"
                    variant={readOnly ? "standard" : "outlined"}
                    value={formData.idNumber ?? ""}
                    data-testid="idNumber"
                    name="idNumber"
                    onChange={handleChange}
                />
                {!validIdNumber && formData.rsaCitizen && (
                    <p data-testid="idNumber-invalid" className="text-danger">Please enter a valid ID number (13 digits).</p>
                )}
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Date of Birth"
                        format="DD/MM/YYYY"
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                required: true,
                                variant: readOnly ? "standard" : "outlined",
                                error: userHasClickedNext && !formData.dob,
                                inputProps: { "data-testid": "dob" },
                            },
                        }}
                        sx={style}
                        name="dob"
                        id="dob"
                        style={{ width: "99%" }}
                        onChange={handleDateChange}
                        defaultValue={formData.dob ? dayjs(formData.dob) : null}
                        maxDate={dayjs()}
                    />
                </LocalizationProvider>
            </Grid>
            <Grid item xs={3}>
                <TextField
                    label="Gender"
                    sx={style}
                    select
                    required
                    variant={readOnly ? "standard" : "outlined"}
                    error={userHasClickedNext && !formData.gender}
                    style={{ width: "99%" }}
                    value={formData.gender ?? 0}
                    data-testid="gender"
                    name="gender"
                    onChange={handleChange}
                >
                    {Gender.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={12}><h5>Contact details</h5></Grid>
            <Grid item xs={3}>
                <MuiTelInput
                    defaultCountry="ZA"
                    sx={style}
                    name="phoneNum"
                    id="phoneNum"
                    style={{ width: "100%" }}
                    label="Phone number"
                    variant={readOnly ? "standard" : "outlined"}
                    inputProps={{ required: true, 'data-testid': "phoneNumber", 'name': "phoneNum" }}
                    error={userHasClickedNext && !formData.phoneNum}
                    value={formData.phoneNum}
                    onChange={handlePhoneNumberChange}
                />
                {!validPhone && (
                    <p className="text-danger">Please enter a valid phone number.</p>
                )}
            </Grid>
            <Grid item xs={3}>
                <TextField
                    style={{ width: "99%" }}
                    sx={style}
                    label="Email address"
                    required
                    error={userHasClickedNext && !formData.email}
                    variant={readOnly ? "standard" : "outlined"}
                    value={formData.email ?? ""}
                    data-testid="email"
                    onChange={handleChange}
                    name="email"
                />
                {!validEmail && (
                    <p className="text-danger">Please enter a valid email address.</p>
                )}
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={8}>
                <FormControlLabel
                    control={<OrangeSwitch />}
                    label="Disabled?"
                    data-testid="disabled"
                    variant={readOnly ? "standard" : "outlined"}
                    labelPlacement="start"
                    value={formData.disabled}
                    checked={formData.disabled}
                    onChange={handleChange}
                    name="disabled"
                />
            </Grid>
            <Grid item xs={4}></Grid>
            <Grid item xs={3}>
                <TextField
                    style={{ width: "99%" }}
                    sx={style}
                    required={formData.disabled}
                    error={formData.disabled && userHasClickedNext && !formData.disabilityDescription}
                    label="Disability description"
                    hidden={!formData.disabled}
                    variant={readOnly ? "standard" : "outlined"}
                    value={formData.disabilityDescription ?? ""}
                    data-testid="disabilityDescription"
                    onChange={handleChange}
                    name="disabilityDescription"
                />
            </Grid>
            <Grid item xs={4}></Grid>
            <Grid item xs={6}>
                <FormControl sx={style} className="inputWidthCV">
                    <TextField
                        sx={style}
                        select
                        name="primaryLanguage"
                        variant={readOnly ? "standard" : "outlined"}
                        required
                        error={userHasClickedNext && !formData.primaryLanguage}
                        label="Primary Language"
                        value={formData.primaryLanguage}
                        data-testid="primaryLanguage"
                        onChange={handleChange}
                    >
                        {ENUMS.primaryLanguage.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </FormControl>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
                <FormControl sx={style} className="inputWidthCV">
                    <Autocomplete
                        multiple
                        data-testid="secondLanguage"
                        id="secondaryLanguage-autocomplete"
                        options={ENUMS.primaryLanguage.map((option) => option.label)}
                        value={secondaryLanguage}
                        onChange={(event, newValue) => handleSecondaryLanguageChange(newValue )}
                        renderInput={(params) => (
                            <TextField
                                data-testid="secondLanguageText"
                                {...params}
                                variant={readOnly ? "standard" : "outlined"}
                                label="Secondary Language(s)"
                            />
                        )}
                        renderTags={(selectedLanguages, getTagProps) =>
                            selectedLanguages.map((language, index) => {
                                const tagProps = getTagProps({ index });
                                return (
                                    <Chip
                                        key={language}
                                        label={ENUMS.primaryLanguage.find((option) => option.value === language)?.label || language}
                                        {...(readOnly ? {} : { onDelete: tagProps.onDelete })}
                                        style={{ margin: 2 }}
                                        disabled={false}
                                    />
                                );
                            })
                        }
                    />
                </FormControl>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
                <TextField
                    style={{ width: "99%" }}
                    sx={style}
                    label="Residential Address"
                    required
                    error={userHasClickedNext && !formData.address}
                    variant={readOnly ? "standard" : "outlined"}
                    value={formData.address ?? ""}
                    data-testid="address"
                    id="address"
                    onChange={handleChange}
                    name="address"
                />
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
                <TextField
                    style={{ width: "99%" }}
                    sx={style}
                    label="City/Postal Code"
                    required
                    error={userHasClickedNext && !formData.city}
                    variant={readOnly ? "standard" : "outlined"}
                    value={formData.city ?? ""}
                    data-testid="city"
                    onChange={handleChange}
                    name="city"
                />
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
                <TextField
                    style={{ width: "99%" }}
                    sx={style}
                    label="Province"
                    variant={readOnly ? "standard" : "outlined"}
                    required
                    error={userHasClickedNext && !formData.province}
                    value={formData.province ?? ""}
                    data-testid="province"
                    onChange={handleChange}
                    name="province"
                />
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
                <FormControl sx={style} className="inputWidthCV">
                    <TextField
                        sx={style}
                        select
                        label="Select Ethnicity"
                        required
                        data-testid="ethnicity"
                        variant={readOnly ? "standard" : "outlined"}
                        error={userHasClickedNext && !formData.ethnicity}
                        name="ethnicity"
                        value={formData.ethnicity ?? 0}
                        onChange={handleChange}
                    >
                        {Ethnicity.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </FormControl>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={3}>
                <TextField
                    sx={style}
                    style={{ width: "99%" }}
                    variant={readOnly ? "standard" : "outlined"}
                    label="LinkedIn profile URL"
                    name="websiteURL"
                    value={formData.websiteURL ?? ""}
                    data-testid="websiteURL"
                    onChange={handleChange}
                    SelectProps={{
                        native: true,
                    }}
                />
            </Grid>
        </Grid>
    );
}